import React, { useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * AutoGrowTextarea - a controlled <textarea> that grows to fit its content instead of scrolling,
 * so a full-sentence value stays fully visible. Used in the board-report inspector drawer for
 * fields that hold prose but don't want the @figure-token machinery of NarrativeTokenField
 * (exec-narrative bullets; driver Cause/Expected/Action notes).
 *
 * Resize is script-driven (ref + useLayoutEffect: reset to "auto" then set to scrollHeight) - the
 * standard browser-robust pattern, not the still-patchy CSS `field-sizing: content`. The effect
 * runs on mount and whenever `value` changes. All sizing/color styling comes from the caller via
 * `className` (the compact drawer scale); this component only adds the auto-grow behavior.
 */
const AutoGrowTextarea = ({
  value,
  onChange,
  disabled,
  ariaLabel,
  placeholder,
  onFocus,
  className,
  id,
  fullWidth,
  minHeightPx,
}) => {
  const ref = useRef(null);

  // width / min-height are set imperatively (via the ref), NOT through a React `style` prop, so they
  // survive re-renders that don't change `value` (React would otherwise re-commit the style object
  // and drop the height this effect sets). `fullWidth` works around this project's overridden
  // theme.width scale, under which `w-full` is a no-op (see CLAUDE.md reference / BulletsField note).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (fullWidth) el.style.width = "100%";
    if (minHeightPx) el.style.minHeight = `${minHeightPx}px`;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value, fullWidth, minHeightPx]);

  return (
    <textarea
      ref={ref}
      id={id}
      rows={1}
      aria-label={ariaLabel}
      className={className}
      value={value || ""}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
    />
  );
};

AutoGrowTextarea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  placeholder: PropTypes.string,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
  // Opt-in: stretch to the container width (w-full is a no-op here) and enforce a minimum height so
  // prose fields invite more than a word. Both default off, so existing callers are unaffected.
  fullWidth: PropTypes.bool,
  minHeightPx: PropTypes.number,
};

export default AutoGrowTextarea;
